<!--
  Read-only: a sent proposal is decided by the ledger that received it, never by this one.
  No accept/decline control appears here (see ReceivedProposalsList).
-->
<script setup lang="ts">
import type { OutgoingRelationshipProposal } from '../internal/types.ts'
import { formatInstantDate, parseInstant } from '@taler/domain'
import { Card, EmptyState, StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LedgerAvatar from '@/features/relationships/components/LedgerAvatar.vue'
import RelationshipIcon from '@/features/relationships/components/RelationshipIcon.vue'

defineProps<{
  proposals: readonly OutgoingRelationshipProposal[]
  ledgerName: (ledgerId: string) => string
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

function requestedOn(proposal: OutgoingRelationshipProposal): string {
  return t('relationships.requestedOn', {
    date: formatInstantDate(parseInstant(proposal.createdAt), currentLocale.value),
  })
}
</script>

<template>
  <section class="flex flex-col gap-[var(--space-block)]">
    <h2
      class="flex items-center gap-2 text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]"
    >
      <RelationshipIcon name="send" />
      {{ t('relationships.sentTitle') }}
      <span v-if="proposals.length > 0" class="text-[color:var(--color-text-muted)]"
        >({{ proposals.length }})</span
      >
    </h2>

    <EmptyState v-if="proposals.length === 0" :title="t('relationships.sentEmpty')">
      <template #icon><RelationshipIcon name="send" /></template>
    </EmptyState>

    <Card v-else padding="none">
      <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
        <li
          v-for="proposal in proposals"
          :key="proposal.id"
          class="flex items-center justify-between gap-3 px-[var(--space-cell)] py-[var(--space-inline)]"
        >
          <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
            <LedgerAvatar :id="proposal.targetLedgerId" />
            <div class="flex min-w-0 flex-col">
              <span
                class="min-w-0 truncate text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]"
              >
                {{ t('relationships.proposalTo', { ledger: ledgerName(proposal.targetLedgerId) }) }}
              </span>
              <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ requestedOn(proposal) }}
              </span>
            </div>
          </div>
          <StatusTag v-if="proposal.status === 'ACCEPTED'" tone="ok" size="sm">
            {{ t('relationships.statusAccepted') }}
          </StatusTag>
          <StatusTag v-else-if="proposal.status === 'DECLINED'" tone="danger" size="sm">
            {{ t('relationships.statusDeclined') }}
          </StatusTag>
          <StatusTag v-else tone="wait" size="sm">{{ t('relationships.statusProposed') }}</StatusTag>
        </li>
      </ul>
    </Card>
  </section>
</template>
